import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockContainer from '../BlockContainer';

import css from './BlockDefault.module.css';
import { Button, NamedLink } from '../../../../components';
import EndorsementModal from '../../../ProfilePage/EndorsementModal';
import { ReactToastify, toastifyFn } from '../../../../components/ReactToastify/ReactToastify';
import { fetchUserEndorsement } from '../../../../ducks/user.duck';
import { insertEndorsementRequest, sendEndorsementEmail } from '../../../ProfilePage/ProfilePage.duck';
import { manageDisableScrolling } from '../../../../ducks/ui.duck';
import { FormattedMessage } from 'react-intl';

const FieldMedia = props => {
  const { className, media, sizes, options } = props;
  const hasMediaField = hasDataInFields([media], options);
  return hasMediaField ? (
    <div className={classNames(className, css.media)}>
      <Field data={media} sizes={sizes} options={options} />
    </div>
  ) : null;
};

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * This returns a component that can render 'defaultBlock' config.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.blockId id from the block config
 * @param {string} props.blockName name from the block config (not used)
 * @param {'defaultBlock'} props.blockType blockType is set to 'defaultBlock'
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.className add more styles in addition to components own css.root
 * @param {string?} props.mediaClassName add styles for the block's attached media field
 * @param {string?} props.textClassName add styles for the block's attached text field
 * @param {string?} props.ctaButtonClass add styles for the block's attached CTA field
 * @param {Object?} props.title heading config for the block
 * @param {Object?} props.text content config for the block (can be markdown)
 * @param {Object?} props.callToAction call to action button (e.g. internal link config)
 * @param {string?} props.responsiveImageSizes
 * @param {Object} props.options extra options for the block component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents Custom fieldComponents
 * @returns {JSX.Element} component that renders block type: 'defaultBlock'
 */
const BlockDefault = props => {
  const [openEndorsementModal, setOpenEndorsementModal] = useState(false);
  const dispatch = useDispatch();
 // Specific selectors instead of returning entire state
 const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
 const currentUser = useSelector(state => state.user?.currentUser);
 const sendEndorsementEmailInProgress = useSelector(state => state.ProfilePage?.sendEndorsementEmailInProgress);
 const sendEndorsementEmailError = useSelector(state => state.ProfilePage?.sendEndorsementEmailError);
 const insertEndorsementRequestInProgress = useSelector(state => state.ProfilePage?.insertEndorsementRequestInProgress);
 const insertEndorsementRequestError = useSelector(state => state.ProfilePage?.insertEndorsementRequestError);
//  const userCreditData = useSelector(state => state.ProfilePage?.userCreditData);

  const {
    blockId,
    className,
    rootClassName,
    mediaClassName,
    textClassName,
    ctaButtonClass,
    title,
    text,
    callToAction,
    media,
    responsiveImageSizes,
    options,
    alignment,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const hasTextComponentFields = hasDataInFields([title, text, callToAction], options);

  const alignmentClasses = {
    left: css.alignLeft,
    center: css.alignCenter,
    right: css.alignRight,
  };

  const alignmentClass = alignmentClasses[alignment];

  const { selectedFeatures } = (currentUser && currentUser.id && currentUser.attributes.profile.protectedData) || {};
  const { company_name } = (currentUser && currentUser.id && currentUser.attributes.profile.publicData) || {};
  const { endorsementRequests } = selectedFeatures || {};

  const getEndorsement = ((endorsementRequests == "Infinity" || endorsementRequests > 0) && company_name);

  const handleEndorsementSubmit = (values) => {
    // 1. Check for existing request first
    return dispatch(fetchUserEndorsement({ email: values.email }))
      .then((res) => {
        const existingRequest = res?.invite_email === values.email;
        if (existingRequest) {
          toastifyFn(
            'info',
            <FormattedMessage id="ProfilePage.duplicateEndorsementRequest" />
          );
          // Stop the chain here
          return Promise.reject(new Error('Duplicate endorsement request'));
        }

        // 2. Prepare email data
        const emailData = {
          ...values,
          company: currentUser?.attributes?.profile?.publicData?.company_name,
          signupUrl: `${process.env.REACT_APP_MARKETPLACE_ROOT_URL}/signup/${values.email}/${currentUser?.id?.uuid}`,
        };

        // 3. First send the email
        return dispatch(sendEndorsementEmail(emailData)).then(() => {
          // 4. After email is sent, insert the request
          return dispatch(insertEndorsementRequest({
            ...emailData,
            userId: currentUser?.id?.uuid,
          }));
        });
      })
      .then(() => {
        // 5. Success toast
        toastifyFn('success', <FormattedMessage id="ProfilePage.sentSuccessfully" />);
        setOpenEndorsementModal(false);
      })
      .catch((error) => {
        if (error.message === 'Duplicate endorsement request') {
          return; // already handled
        }
        console.error('Failed to process endorsement request:', error);
        toastifyFn('error', <FormattedMessage id="ProfilePage.endorsementFailed" />);
      });
  }


  const getEndorsements = <>
    <EndorsementModal
      isOpen={openEndorsementModal}
      onClose={() => setOpenEndorsementModal(false)}
      onSubmit={handleEndorsementSubmit}
      sendEndorsementEmailInProgress={sendEndorsementEmailInProgress || insertEndorsementRequestInProgress}
      sendEndorsementEmailError={sendEndorsementEmailError || insertEndorsementRequestError}
      onManageDisableScrolling={(componentId, disableScrolling) => dispatch(manageDisableScrolling(componentId, disableScrolling))}
    />
    <ReactToastify />
  </>

  return (
    <BlockContainer id={blockId} className={classes}>
      <FieldMedia
        media={media}
        sizes={responsiveImageSizes}
        className={mediaClassName}
        options={options}
      />
      {hasTextComponentFields ? (
        <div className={classNames(textClassName, alignmentClass, css.text)}>
          <Field data={title} options={options} />
          <Field data={text} options={options} />
          <Field data={callToAction} className={ctaButtonClass} options={options} />

          {blockId == "hero_section-block-1" && <div className={css.actionButtons}>
            {isAuthenticated ? (
              <>
                {getEndorsement ? <Button className={css.ctaButtons} onClick={() => setOpenEndorsementModal(true)}>Get Endorsements</Button> : <NamedLink
                  name={!company_name && (endorsementRequests == "Infinity" || endorsementRequests > 0) ? "ProfileSettingsPage" : "SubscriptionPage"}
                  className={css.ctaButton}
                >
                  <FormattedMessage id="TopbarDesktop.getEndorsements" />
                </NamedLink>}
                <NamedLink name="NewListingPage" className={css.ctaButton}>
                  <FormattedMessage id="TopbarDesktop.postAProject" />
                </NamedLink>
              </>
            ) : (
              <>
                <NamedLink name="SignupPage" className={css.ctaButton}>
                  Signup
                </NamedLink>
                <NamedLink
                  name="CMSPage"
                  params={{ pageId: 'contract-owner' }}
                  className={css.ctaButton}
                >
                  For Contract Owners
                </NamedLink>
              </>
            )}
          </div>}
          {getEndorsements}
        </div>
      ) : null}
    </BlockContainer>
  );
};

export default BlockDefault;
